import { API_BASE_URL } from '../config';

export interface ContractTypeInfo {
  id: string;
  nameToUse: string;
  description: string;
  category: string;
  subCategory: string;
  internalName: string;
}

/**
 * Fetch available contract types from the API
 */
export const getContractTypes = async (): Promise<ContractTypeInfo[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/read/contract-types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`HTTP error! status: ${response.status}, body:`, errorText);
      throw new Error(`Failed to fetch contract types: ${response.status} ${response.statusText}`);
    }

    // Check if response is actually JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Response is not JSON:', text.substring(0, 200));
      throw new Error('Server returned non-JSON response. The endpoint may not be configured correctly.');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching contract types:', error);
    // Return empty array instead of throwing to allow UI to still render
    if (error instanceof Error && error.message.includes('JSON')) {
      console.warn('Returning empty array due to JSON parsing error');
      return [];
    }
    throw error;
  }
};

/**
 * Upload and analyze a file using the specified contract type
 */
export const uploadFile = async (
  contractType: string,
  file: File,
): Promise<{ content: string; analysisId: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.idToken) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/read/analyze/${contractType}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${user.idToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { content: data.content, analysisId: data.analysisId };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}; 